import {
  FIRST_ROUND_INDUSTRY_PAGES,
  FIRST_ROUND_LEGAL_PAGES,
  type FirstRoundLegalPath,
} from "@reactive-resume/utils";
import { type ComponentType, lazy, type ReactNode, Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  type LoaderFunction,
  Navigate,
  Route,
  useLocation,
} from "react-router-dom";

// Eagerly loaded — needed on the public home page or auth flow
import { AccountLinkConflictPage } from "../pages/auth/account-link-conflict/page";
import { ClerkAuthLayout } from "../pages/auth/clerk-layout";
import { CompleteProfilePage } from "../pages/auth/complete-profile/page";
import { ClerkLoginPage } from "../pages/auth/login/clerk-page";
import { ClerkRegisterPage } from "../pages/auth/register/page";
import { BlogPage } from "../pages/blog/page";
import { BlogArticlePage } from "../pages/blog/post";
// Loaders must be eagerly imported (used directly in route config, not rendered)
import { builderLoader } from "../pages/builder/page";
import { CareersPage } from "../pages/careers/page";
import { ContactPage } from "../pages/contact/page";
import { PracticeRouteError } from "../pages/dashboard/practice-interview/notebook/RouteError";
import { HomeLayout, MarketingLayout } from "../pages/home/layout";
import { HomePage } from "../pages/home/page";
import { HubPage } from "../pages/hub/page";
import { PrivacyPolicyPage } from "../pages/legal/privacy/page";
import { TermsOfServicePage } from "../pages/legal/terms/page";
import { publicLoader, PublicResumePage } from "../pages/public/page";
import { Providers } from "../providers";
import { prefetchProductModules } from "../services/product-modules";
import { paymentLoader } from "../services/resume/payment";
import { isFirstRoundMarketingHost } from "../utils/firstRoundAppUrl";
import { AuthGuard } from "./guards/auth";
import { EmailVerifiedGuard } from "./guards/emailVerify";
import { ModuleGuard } from "./guards/module";
import { LeaveMarketingHost, MovedToFirstRound, PricingRedirect } from "./leave-marketing-host";

// Catches "Failed to fetch dynamically imported module" (stale chunk after redeploy)
// and forces a full page reload so the browser picks up the new bundle.
function lazyWithReload<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (err instanceof TypeError && typeof window !== "undefined") {
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw err;
    }),
  );
}

// Lazily loaded — only needed inside the authenticated dashboard / builder
const BuilderLayout = lazyWithReload(() =>
  import("../pages/builder/layout").then((m) => ({ default: m.BuilderLayout })),
);
const BuilderPage = lazyWithReload(() =>
  import("../pages/builder/page").then((m) => ({ default: m.BuilderPage })),
);
const ApplicationStatus = lazyWithReload(() =>
  import("../pages/dashboard/applicationStatus").then((m) => ({ default: m.ApplicationStatus })),
);
const FeedbackPage = lazyWithReload(() =>
  import("../pages/dashboard/feedback/page").then((m) => ({ default: m.FeedbackPage })),
);
const LiveKitInterviewPage = lazyWithReload(() =>
  import("../pages/dashboard/interview/LiveKitInterviewPage").then((m) => ({ default: m.default })),
);
const InterviewsPage = lazyWithReload(() =>
  import("../pages/dashboard/interviews/InterviewsPage").then((m) => ({ default: m.default })),
);
const InterviewVerification = lazyWithReload(() =>
  import("../pages/dashboard/interviewVerification").then((m) => ({
    default: m.InterviewVerification,
  })),
);
const JobSearch = lazyWithReload(() =>
  import("../pages/dashboard/jobs").then((m) => ({ default: m.JobSearch })),
);
const DashboardLayout = lazyWithReload(() =>
  import("../pages/dashboard/layout").then((m) => ({ default: m.DashboardLayout })),
);
const DashboardOverviewPage = lazyWithReload(() =>
  import("../pages/dashboard/overview/page").then((m) => ({ default: m.DashboardOverviewPage })),
);
const SpotlightIntroPage = lazyWithReload(() =>
  import("../pages/dashboard/spotlight-intro/page").then((m) => ({
    default: m.SpotlightIntroPage,
  })),
);
const SpotlightDetailPage = lazyWithReload(() =>
  import("../pages/dashboard/spotlight-intro/detail").then((m) => ({ default: m.default })),
);
const ResumesPage = lazyWithReload(() =>
  import("../pages/dashboard/resumes/page").then((m) => ({ default: m.ResumesPage })),
);
const SettingsPage = lazyWithReload(() =>
  import("../pages/dashboard/settings/page").then((m) => ({ default: m.SettingsPage })),
);
const ShareJobs = lazyWithReload(() =>
  import("../pages/shareJobs").then((m) => ({ default: m.ShareJobs })),
);
const BoardPage = lazyWithReload(() =>
  import("../pages/board").then((m) => ({ default: m.BoardPage })),
);
const NotificationsPage = lazyWithReload(() =>
  import("../pages/dashboard/notifications/page").then((m) => ({ default: m.default })),
);
// Practice Interview "coach's notebook": Home, Prep, the live call, Feedback and History.
const PracticeInterviewPage = lazyWithReload(() =>
  import("../pages/dashboard/practice-interview/notebook/HomePage").then((m) => ({
    default: m.default,
  })),
);
const PracticeSessionPage = lazyWithReload(() =>
  import("../pages/dashboard/practice-interview/notebook/SessionPage").then((m) => ({
    default: m.default,
  })),
);
const PracticeFeedbackPage = lazyWithReload(() =>
  import("../pages/dashboard/practice-interview/notebook/FeedbackPage").then((m) => ({
    default: m.default,
  })),
);
const PracticePrepPage = lazyWithReload(() =>
  import("../pages/dashboard/practice-interview/notebook/PrepPage").then((m) => ({
    default: m.default,
  })),
);
// The 2-minute demo inside the app, for Free users (First Round plans).
const PracticeDemoAppPage = lazyWithReload(() =>
  import("../pages/dashboard/practice-interview/demo/page").then((m) => ({
    default: m.default,
  })),
);
const PracticeHistoryPage = lazyWithReload(() =>
  import("../pages/dashboard/practice-interview/notebook/HistoryPage").then((m) => ({
    default: m.default,
  })),
);
const CareerParkPage = lazyWithReload(() =>
  import("../pages/dashboard/career-park/page").then((m) => ({ default: m.CareerParkPage })),
);
const CareerPageEditorPage = lazyWithReload(() =>
  import("../pages/dashboard/career-page/page").then((m) => ({ default: m.CareerPageEditorPage })),
);
const PublicCareerPage = lazyWithReload(() =>
  import("../pages/public/career-page").then((m) => ({ default: m.PublicCareerPage })),
);

const L = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={null}>{children}</Suspense>
);

const PracticeInterviewMarketingPage = lazyWithReload(() =>
  import("../pages/practice-interview/page").then((m) => ({
    default: m.PracticeInterviewMarketingPage,
  })),
);

const InstitutionsPage = lazyWithReload(() =>
  import("../pages/institutions/page").then((m) => ({ default: m.InstitutionsPage })),
);

const FirstRoundPage = lazyWithReload(() =>
  import("../pages/first-round/page").then((m) => ({ default: m.FirstRoundPage })),
);

const FirstRoundLegalRoute = lazyWithReload(() =>
  import("../pages/first-round/legal/page").then((m) => ({ default: m.FirstRoundLegalRoute })),
);

const FirstRoundDemoResultsPage = lazyWithReload(() =>
  import("../pages/first-round/demo-results/page").then((m) => ({
    default: m.FirstRoundDemoResultsPage,
  })),
);

// React.lazy, not lazyWithReload: that helper only takes prop-less components.
const FirstRoundIndustryLayout = lazy(() =>
  import("../pages/first-round/industry-layout").then((m) => ({
    default: m.FirstRoundIndustryLayout,
  })),
);

// Every path in FIRST_ROUND_LEGAL_PAGES renders FirstRoundLegalRoute (the hub or one document).
const firstRoundLegalPaths = Object.keys(FIRST_ROUND_LEGAL_PAGES) as FirstRoundLegalPath[];

// Components for the First Round industry pages (FIRST_ROUND_INDUSTRY_PAGES in libs/utils).
const FIRST_ROUND_INDUSTRY_COMPONENTS: Record<string, ComponentType> = {
  "/for-educational-institutes": InstitutionsPage,
};

const pageFallback = <div className="min-h-screen" aria-hidden="true" />;

// The dashboard also needs the menu switches before its first paint, or the sidebar would show
// every item and then drop the hidden ones.
const dashboardLoader: LoaderFunction = async (args) => {
  const [payment] = await Promise.all([paymentLoader(args), prefetchProductModules()]);
  return payment;
};

const firstRoundIndustryRoutes = Object.keys(FIRST_ROUND_INDUSTRY_PAGES).map((path) => {
  const Page = FIRST_ROUND_INDUSTRY_COMPONENTS[path];
  // Fail loudly: without a component the page would bounce to theresume.io on the brand domain.
  // eslint-disable-next-line lingui/no-unlocalized-strings -- developer error, not UI copy
  if (!Page) throw new Error(`No component registered for First Round page ${path}`);
  return (
    <Route
      key={path}
      path={path}
      element={
        <MovedToFirstRound>
          <Suspense fallback={pageFallback}>
            <FirstRoundIndustryLayout>
              <Page />
            </FirstRoundIndustryLayout>
          </Suspense>
        </MovedToFirstRound>
      }
    />
  );
});

// Sends a campaign short link to a page without dropping its utm_* query.
const RedirectKeepingQuery = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate replace to={{ pathname: to, search }} />;
};

// The First Round marketing hosts (firstround.com.au, www, firstround.theresume.io) are pure
// marketing: they serve the First Round page at "/" plus the industry pages, and send every other
// path to theresume.io (the server 301s full loads; LeaveMarketingHost covers in-app links).
// Elsewhere the page lives at /first-round. The pages bring their own First Round header and
// footer, so they sit outside HomeLayout.
const firstRoundMarketingRoutes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route
      path="/"
      element={
        <Suspense fallback={pageFallback}>
          <FirstRoundPage />
        </Suspense>
      }
    />
    {/* Email campaign link. The server 302s full loads to "/"; this covers in-app navigation. */}
    <Route path="/wam-70" element={<RedirectKeepingQuery to="/" />} />
    {firstRoundIndustryRoutes}
    {/* The First Round legal set: /legal, /terms, /privacy, /trial-terms, /trial-privacy. */}
    {firstRoundLegalPaths.map((path) => (
      <Route
        key={path}
        path={path}
        element={
          <Suspense fallback={pageFallback}>
            <FirstRoundLegalRoute />
          </Suspense>
        }
      />
    ))}
    <Route path="*" element={<LeaveMarketingHost />} />
  </Route>,
);

export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route
      path="/first-round"
      element={
        <MovedToFirstRound>
          <Suspense fallback={pageFallback}>
            <FirstRoundPage />
          </Suspense>
        </MovedToFirstRound>
      }
    />
    {/* The First Round email link on app hosts (the server 302s full loads the same way). */}
    <Route path="/wam-70" element={<RedirectKeepingQuery to="/first-round" />} />
    {firstRoundIndustryRoutes}
    {/* In-app copies of the legal set (staging, localhost); production theresume.io sends them
        to firstround.com.au. */}
    {firstRoundLegalPaths.map((path) => (
      <Route
        key={path}
        path={`/first-round${path}`}
        element={
          <MovedToFirstRound>
            <Suspense fallback={pageFallback}>
              <FirstRoundLegalRoute />
            </Suspense>
          </MovedToFirstRound>
        }
      />
    ))}
    {/* The First Round demo's transcript and feedback, after sign-up. It handles its own
        First Round-branded sign-up redirect and the profile step. */}
    <Route
      path="/demo/practice/:sessionId"
      element={
        <Suspense fallback={pageFallback}>
          <FirstRoundDemoResultsPage />
        </Suspense>
      }
    />
    {/* The hub: theresume.io's front door, which sends job seekers to First Round and hiring
        teams to Apex 25. It brings its own header and footer, so it sits outside HomeLayout. */}
    <Route path="/" element={<HubPage />} />
    <Route element={<HomeLayout />}>
      <Route element={<MarketingLayout />}>
        {/* The job seeker story that used to be the homepage. */}
        <Route path="/job-seekers" element={<HomePage />} />
        <Route
          path="/practice-interview"
          element={
            <MovedToFirstRound>
              <Suspense fallback={pageFallback}>
                <PracticeInterviewMarketingPage />
              </Suspense>
            </MovedToFirstRound>
          }
        />
      </Route>
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/careers" element={<CareersPage />} />
    </Route>

    <Route path="auth">
      <Route element={<ClerkAuthLayout />}>
        <Route path="login" element={<ClerkLoginPage />} />
        <Route path="register" element={<ClerkRegisterPage />} />
        <Route path="account-link-conflict" element={<AccountLinkConflictPage />} />
        {/* Onboarding for new Clerk sign-ups: name/username + legal consents */}
        <Route path="complete-profile" element={<CompleteProfilePage />} />
      </Route>

      <Route index element={<Navigate to="/auth/login" replace />} />
    </Route>

    <Route path="dashboard">
      <Route element={<AuthGuard />}>
        <Route
          element={
            <L>
              <DashboardLayout />
            </L>
          }
          loader={dashboardLoader}
        >
          <Route
            path="settings"
            element={
              <L>
                <SettingsPage />
              </L>
            }
          />

          <Route element={<EmailVerifiedGuard />}>
            <Route
              index
              element={
                <L>
                  <DashboardOverviewPage />
                </L>
              }
            />
            <Route element={<ModuleGuard module="feedback_action_hub" />}>
              <Route
                path="feedback"
                element={
                  <L>
                    <FeedbackPage />
                  </L>
                }
              />
            </Route>
            <Route element={<ModuleGuard module="resumes" />}>
              <Route
                path="resumes"
                element={
                  <L>
                    <ResumesPage />
                  </L>
                }
              />
            </Route>
            <Route element={<ModuleGuard module="spotlight_intro" />}>
              <Route
                path="spotlight-intro"
                element={
                  <L>
                    <SpotlightIntroPage />
                  </L>
                }
              />
              <Route
                path="spotlight-intro/:id"
                element={
                  <L>
                    <SpotlightDetailPage />
                  </L>
                }
              />
            </Route>
            <Route element={<ModuleGuard module="notifications" />}>
              <Route
                path="notifications"
                element={
                  <L>
                    <NotificationsPage />
                  </L>
                }
              />
            </Route>
            {/* Only the Interview Requests list is gated. theRecruit's invite emails link to
                verify/:id, which leads to the interview room (:interviewId): both stay reachable
                with the module hidden, so invited candidates can still take their interview. */}
            <Route path="interview">
              <Route element={<ModuleGuard module="interview_requests" />}>
                <Route
                  index
                  element={
                    <L>
                      <InterviewsPage />
                    </L>
                  }
                />
              </Route>
              <Route
                path=":interviewId"
                element={
                  <L>
                    <LiveKitInterviewPage />
                  </L>
                }
              />
              <Route path="verify">
                <Route
                  path=":interviewRequestId"
                  element={
                    <L>
                      <InterviewVerification />
                    </L>
                  }
                />
              </Route>
            </Route>
            <Route element={<ModuleGuard module="job_search" />}>
              <Route path="jobs">
                <Route
                  index
                  element={
                    <L>
                      <JobSearch />
                    </L>
                  }
                />
                <Route
                  path=":jobId"
                  element={
                    <L>
                      <ShareJobs />
                    </L>
                  }
                />
              </Route>
            </Route>
            <Route element={<ModuleGuard module="application_status" />}>
              <Route path="application-status">
                <Route
                  index
                  element={
                    <L>
                      <ApplicationStatus />
                    </L>
                  }
                />
              </Route>
            </Route>
            <Route element={<ModuleGuard module="practice_interview" />}>
              <Route path="practice-interview" errorElement={<PracticeRouteError />}>
                <Route
                  index
                  element={
                    <L>
                      <PracticeInterviewPage />
                    </L>
                  }
                />
                <Route
                  path="preview"
                  element={
                    <L>
                      <PracticePrepPage />
                    </L>
                  }
                />
                <Route
                  path="session/:sessionId"
                  element={
                    <L>
                      <PracticeSessionPage />
                    </L>
                  }
                />
                <Route
                  path="results/:sessionId"
                  element={
                    <L>
                      <PracticeFeedbackPage />
                    </L>
                  }
                />
                <Route
                  path="history"
                  element={
                    <L>
                      <PracticeHistoryPage />
                    </L>
                  }
                />
                <Route
                  path="demo"
                  element={
                    <L>
                      <PracticeDemoAppPage />
                    </L>
                  }
                />
              </Route>
            </Route>
            <Route element={<ModuleGuard module="career_quest" />}>
              <Route
                path="career-park"
                element={
                  <L>
                    <CareerParkPage />
                  </L>
                }
              />
            </Route>
            <Route element={<ModuleGuard module="career_page" />}>
              <Route
                path="career-page"
                element={
                  <L>
                    <CareerPageEditorPage />
                  </L>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>

    {/* Public Job Board Routes */}
    <Route path="board">
      <Route
        path=":boardSlug"
        element={
          <L>
            <BoardPage />
          </L>
        }
      />
      <Route
        path=":boardSlug/job/:jobId"
        element={
          <L>
            <ShareJobs />
          </L>
        }
      />
      <Route
        path=":boardSlug/:companySlug"
        element={
          <L>
            <BoardPage />
          </L>
        }
      />
      <Route
        path=":boardSlug/:companySlug/:jobSlug"
        element={
          <L>
            <ShareJobs />
          </L>
        }
      />
    </Route>

    <Route path="open-positions">
      <Route
        path=":jobId"
        index
        element={
          <L>
            <ShareJobs />
          </L>
        }
      />
    </Route>

    <Route path="builder">
      <Route element={<AuthGuard />}>
        <Route element={<EmailVerifiedGuard />}>
          <Route
            element={
              <L>
                <BuilderLayout />
              </L>
            }
          >
            <Route element={<ModuleGuard module="resumes" />}>
              <Route
                path=":id"
                loader={builderLoader}
                element={
                  <L>
                    <BuilderPage />
                  </L>
                }
              />
            </Route>

            <Route index element={<Navigate to="/dashboard/resumes" replace />} />
          </Route>
        </Route>
      </Route>
    </Route>

    {/* theResume sells the First Round plans (plan dialog); old /pricing links redirect. */}
    <Route path="pricing" element={<PricingRedirect />} />

    <Route path="contact">
      <Route index element={<ContactPage />} />
    </Route>

    {/* Shared Career Page — must be defined before /:username/:slug.
        The /u/ prefix also prevents collision with public resumes. */}
    <Route path="u">
      <Route element={<AuthGuard />}>
        <Route
          path=":username"
          element={
            <L>
              <PublicCareerPage />
            </L>
          }
        />
      </Route>
    </Route>

    {/* Public Routes */}
    <Route path=":username">
      <Route path=":slug" loader={publicLoader} element={<PublicResumePage />} />
    </Route>
  </Route>,
);

export const router = createBrowserRouter(
  isFirstRoundMarketingHost() ? firstRoundMarketingRoutes : routes,
);
